// API catalog for the inv-api backend (GM-OPEN).
// This file is the source for the in-app API Documentation page.
// Keep this in sync with API_DOCUMENTATION.md whenever endpoints change.

export const API_METADATA = {
  serviceName: "inv-api (GM-OPEN)",
  baseUrlDev: "https://mobileapi.clayo.io/api",
  baseUrlLocal: "http://localhost:3001/api",
  healthCheck: "GET /ping → pong",
  authHeader: "Authorization: <accessToken>  (no 'Bearer ' prefix)",
  refreshEndpoint: "POST /users/refresh",
};

export const CATEGORIES = [
  "Authentication & Users",
  "OTPs",
  "Profile & Lookups",
  "Subscriptions",
  "Subscribers",
  "Investments / Applications",
  "Redemptions",
  "Products & Catalog",
  "Product Performance",
  "Categories",
  "News",
  "Configurations",
  "FAQs",
  "Documents",
  "Notifications",
  "Notification Settings",
  "Promotions / Offers",
  "Rewards / Coins",
  "Dates For Your Diary",
  "Support / Tickets",
  "Feedback",
  "Interests",
  "Invite Friends",
  "Seek (Leads)",
  "Brokers / Introducers",
  "Payments",
  "Requests / Mail",
  "Analytics",
  "Dashboard (ETL)",
  "Admin / FST",
  "Webhooks (CRM / DocuSeal)",
  "Health / Misc",
];

// Auth levels:
//   Public     – no auth
//   JWT        – any authenticated user
//   user       – role 'user' (investor)
//   broker     – role 'broker'
//   admin      – role 'admin'
//   broker|user
//   admin|broker|user
//   FST        – static header token (Dashboard ETL)
export const APIS = [
  // ───────── Authentication & Users ─────────
  {
    id: "users-login",
    method: "POST",
    path: "/users/login",
    category: "Authentication & Users",
    auth: "Public",
    title: "Mobile login",
    description:
      "Email+password OR PIN+deviceId login. Issues an access + refresh token pair. 3 failed attempts locks the account; an auto-unlock job runs after 30 min. FST users receive an additional Twilio OTP step (response includes isFST=true).",
    request: {
      body: {
        email: "string",
        password: "string",
        deviceId: "string (optional, for PIN flow)",
        pin: "string (optional)",
      },
    },
    response: {
      success: true,
      result: {
        accessToken: "JWT",
        refreshToken: "JWT",
        user: "{ id, role, isFST, ... }",
      },
    },
    notes: [
      "Calls Twilio Verify SID for FST users",
      "Writes to logInHistory / userFailures tables",
      "Queues user_queue.unLockUser when locking",
    ],
    consumers: {
      mobile: [
        "api/user.js > loginServer",
        "screens/auth/signIn.js",
        "screens/auth/signUpOtp.js",
      ],
      web: [],
    },
    controller: "userController.login",
  },
  {
    id: "users-weblogin",
    method: "POST",
    path: "/users/webLogin",
    category: "Authentication & Users",
    auth: "Public",
    title: "Admin web login",
    description:
      "Admin-only login for the web console. Enforces role === 'admin'. Same locking rules as /users/login.",
    request: { body: { email: "string", password: "string" } },
    response: {
      success: true,
      result: { accessToken: "JWT", refreshToken: "JWT" },
    },
    consumers: {
      mobile: [],
      web: ["src/apis/loginApi.js > loginServer", "Admin login page"],
    },
    controller: "userController.webLogin",
  },
  {
    id: "users-refresh",
    method: "POST",
    path: "/users/refresh",
    category: "Authentication & Users",
    auth: "Public (refresh token in body)",
    title: "Rotate access/refresh tokens",
    description:
      "Exchanges a valid refresh token for a fresh access + refresh token pair. Refresh tokens are encrypted at rest using ENCRYPT_KEY.",
    request: { body: { refreshToken: "string" } },
    response: {
      success: true,
      result: { accessToken: "JWT", refreshToken: "JWT" },
    },
    consumers: {
      mobile: ["api/user.js > refreshToken", "axios interceptor in apikit.js"],
      web: ["src/Allapis/Apikit/apikit.js (silent refresh interceptor)"],
    },
    controller: "userController.refreshTokenMiddleWare",
  },
  {
    id: "users-register",
    method: "POST",
    path: "/users/regsiter",
    category: "Authentication & Users",
    auth: "Public",
    title: "Self-onboarding register (sic typo)",
    description:
      "Creates a user + userInfo row. Looks up CRM via checkCrmBroker, sends welcome email through Azure ACS, creates referral row, sends mobile OTP via Twilio. NOTE: path intentionally misspelled 'regsiter' for backward compatibility.",
    consumers: {
      mobile: [
        "api/user.js > registerServer",
        "screens/auth/signUp.js (onboarding flows)",
      ],
      web: [],
    },
    notes: ["Pushes contact_queue.fetchInvestor job"],
    controller: "userController.create",
  },
  {
    id: "users-verifyEmail",
    method: "POST",
    path: "/users/verifyEmail",
    category: "Authentication & Users",
    auth: "Public",
    title: "Verify email token",
    description: "Validates the email-token sent during signup.",
    consumers: {
      mobile: ["api/user.js > verifyEmail (signup, profile email change)"],
      web: [],
    },
    controller: "userController.verifyEmailMiddleWare",
  },
  {
    id: "users-forgot",
    method: "POST",
    path: "/users/forgot",
    category: "Authentication & Users",
    auth: "Public",
    title: "Forgot password",
    description:
      "Generates an OTP and sends a forgot-password email via Azure Communication Email.",
    consumers: {
      mobile: [
        "api/user.js > forgotPassword",
        "screens/auth/forgotPassword.js",
      ],
      web: ["src/apis/otpVerify.js > resetPassword"],
    },
    controller: "userController.forgotPasswordMiddleWare",
  },
  {
    id: "users-validate",
    method: "POST",
    path: "/users/validate",
    category: "Authentication & Users",
    auth: "Public",
    title: "Validate OTP (signup / forgot / mobileVerify)",
    description:
      "Validates the OTP for signup, forgot-password, or mobile-verify flows. For brokers it triggers contact_queue.updateIntroducer to push mobile-verified state to CRM.",
    consumers: {
      mobile: ["api/user.js > validateOtp", "OTP screens, signUpOtp.js"],
      web: ["src/apis/otpVerify.js > OtpVerification"],
    },
    controller: "userController.validateOTPMiddleWare",
  },
  {
    id: "users-reset-put",
    method: "PUT",
    path: "/users/reset",
    category: "Authentication & Users",
    auth: "Public (OTP-protected)",
    title: "Reset password",
    description: "Sets a new password after a successful OTP validation.",
    consumers: {
      mobile: ["api/user.js > resetPassword", "screens/auth/passwordUpdate.js"],
      web: ["src/apis/otpVerify.js > updatePassword"],
    },
    controller: "userController.resetPasswordMiddleWare",
  },
  {
    id: "users-brokerPass",
    method: "PUT",
    path: "/users/brokerPass",
    category: "Authentication & Users",
    auth: "Public",
    title: "Broker first-time password set",
    description:
      "Broker sets initial password from broker-invitation email link.",
    consumers: {
      mobile: ["api/user.js > ChangePassword (broker settings)"],
      web: [],
    },
    controller: "userController.updateBrokerPassMiddleWare",
  },
  {
    id: "users-authInfo",
    method: "GET",
    path: "/users/authInfo/:id/:deviceId",
    category: "Authentication & Users",
    auth: "Public",
    title: "Validate biometric on device",
    description:
      "Verifies whether a device is enrolled for biometric authentication.",
    consumers: {
      mobile: ["api/user.js > fingerServer (biometric sign-in, app startup)"],
      web: [],
    },
    controller: "userController.fingerPrintMiddleWare",
  },
  {
    id: "users-request",
    method: "POST",
    path: "/users/request",
    category: "Authentication & Users",
    auth: "Public",
    title: "Contact-us request",
    description:
      "Generic contact-us / introducer onboarding request submission.",
    consumers: {
      mobile: ["api/user.js > generateINTRRequest"],
      web: [],
    },
    controller: "userController.requestMiddleware",
  },
  {
    id: "users-list",
    method: "GET",
    path: "/users/list",
    category: "Authentication & Users",
    auth: "JWT (admin)",
    title: "Admin user list",
    description:
      "Returns paginated/filterable admin user list (name, email, role, date range).",
    consumers: {
      mobile: [],
      web: ["src/apis/usersApis.js > getUserList", "Users grid"],
    },
    controller: "userController.getUsersMiddleWare",
  },
  {
    id: "users-info",
    method: "GET",
    path: "/users/info/:id",
    category: "Authentication & Users",
    auth: "JWT",
    title: "Read-only view of another user",
    description:
      "Returns full user info for admin/broker viewing a customer profile.",
    consumers: {
      mobile: [],
      web: [
        "src/apis/usersApis.js > getUserDetails",
        "src/apis/usersApis.js > getUserLoginHistory",
        "User detail page",
      ],
    },
    controller: "userController.getUserInfoViewMiddleWare",
  },
  {
    id: "users-upload",
    method: "POST",
    path: "/users/upload",
    category: "Authentication & Users",
    auth: "JWT",
    title: "Upload avatar",
    description:
      "Uploads a user avatar to Azure Blob and returns the public URL.",
    consumers: {
      mobile: ["Avatar upload via profile screens"],
      web: ["Admin avatar upload"],
    },
    controller: "userController.updateImageMiddleWare",
  },
  {
    id: "users-admin",
    method: "POST",
    path: "/users/admin",
    category: "Authentication & Users",
    auth: "admin",
    title: "Create new admin user",
    description: "Creates a new admin role user from the admin web console.",
    consumers: { mobile: [], web: ["Admin user management"] },
    controller: "userController.createNewAdminMiddleWare",
  },
  {
    id: "users-update",
    method: "PUT",
    path: "/users/update",
    category: "Authentication & Users",
    auth: "JWT",
    title: "Update profile (CRM sync)",
    description:
      "Updates user profile. Triggers contact_queue.updateContact to sync changes with Dynamics 365 CRM.",
    consumers: {
      mobile: [],
      web: [
        "src/apis/usersApis.js > updateUsers (admin updates own profile, auto-adds type: 'profile')",
      ],
    },
    controller: "userAdditionalController.updateUserProfile",
  },
  {
    id: "users-pin",
    method: "PUT",
    path: "/users/pin",
    category: "Authentication & Users",
    auth: "broker|user",
    title: "Update device PIN",
    description:
      "Updates the device PIN. Stored bcrypt-hashed in userDevices.pin. FST-blocked.",
    fstBlocked: true,
    consumers: {
      mobile: ["api/user.js > updatePin (screens/auth/pinLogin.js, settings)"],
      web: [],
    },
    controller: "devicesController",
  },
  {
    id: "users-biometric",
    method: "PUT",
    path: "/users/biometric",
    category: "Authentication & Users",
    auth: "broker|user",
    title: "Enable/disable biometric",
    description: "Toggles biometric authentication for a device. FST-blocked.",
    fstBlocked: true,
    consumers: {
      mobile: ["api/user.js > updateBiometric (Settings → biometric toggle)"],
      web: [],
    },
    controller: "devicesController",
  },
  {
    id: "users-accountDel",
    method: "PUT",
    path: "/users/accountDel",
    category: "Authentication & Users",
    auth: "broker|user",
    title: "Soft delete account",
    description:
      "OTP-protected soft delete of the user's account. FST-blocked.",
    fstBlocked: true,
    consumers: {
      mobile: [
        "api/user.js > deleteAccount (screens/settings/accountDeletion.js)",
      ],
      web: [],
    },
    controller: "userController.userAccountDeletionMiddleWare",
  },
  {
    id: "users-block-status",
    method: "PUT",
    path: "/users/:id",
    category: "Authentication & Users",
    auth: "JWT (admin)",
    title: "Block / unblock user",
    description: "Block/unblock or unlock a user account.",
    consumers: {
      mobile: [],
      web: ["src/apis/usersApis.js > userBlockUnblock"],
    },
    controller: "userController.userBlockStatusMiddleWare",
  },
  {
    id: "users-unlock",
    method: "PUT",
    path: "/users/unlock/:id",
    category: "Authentication & Users",
    auth: "JWT (admin)",
    title: "Unlock locked account",
    description:
      "Resets the locked status of a user that hit the failed-attempts threshold.",
    consumers: { mobile: [], web: ["src/apis/usersApis.js > userUnlock"] },
    controller: "userController",
  },
  {
    id: "users-by-id",
    method: "GET",
    path: "/users/:id",
    category: "Authentication & Users",
    auth: "broker|user",
    title: "Get user by id",
    description: "Returns the user's profile (own profile, or via permission).",
    consumers: {
      mobile: ["api/user.js > getUserData (broker views of investors)"],
      web: [
        "src/apis/usersApis.js > editUsers",
        "src/apis/usersApis.js > updateUserspro",
        "src/apis/profileInfoApi.js > getuserProfile (⚠ broken: calls response.json())",
      ],
    },
    controller: "userController.getUserInfoMiddleWare",
  },
  {
    id: "users-countries",
    method: "GET",
    path: "/users/countries",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Countries list",
    description: "All countries from CRM / local cache.",
    consumers: {
      mobile: [
        "api/user.js > countryList (KYC, profile, phone-country picker)",
      ],
      web: [],
    },
    controller: "countryController",
  },
  {
    id: "users-county",
    method: "GET",
    path: "/users/county/:id",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Counties by country id",
    description: "Returns counties for a given country id.",
    consumers: {
      mobile: ["api/user.js > countyList (KYC / address forms)"],
      web: [],
    },
    controller: "countryController",
  },
  {
    id: "users-address-options",
    method: "GET",
    path: "/users/address/options",
    category: "Profile & Lookups",
    auth: "user",
    title: "Auto-address (postcode → suggestions)",
    description:
      "Wraps an external auto-address provider (lib/autoAddress.js). Returns suggestions for a postcode.",
    consumers: {
      mobile: ["api/postalCodeApi.js > getPostalCode"],
      web: [],
    },
    controller: "userController.getAutoAddressOptionsMiddleWare",
  },
  {
    id: "users-profile-get",
    method: "GET",
    path: "/users/profile",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Get user profile section",
    description:
      "Returns full profile or a specific section via ?type=personal|address|nationality|occupation|tax|bank.",
    consumers: {
      mobile: ["api/user.js > getUserProfile (profile screens, drawer load)"],
      web: [],
    },
    controller: "userController.getUserProfileInfoMiddleWare",
  },
  {
    id: "users-profile-put",
    method: "PUT",
    path: "/users/profile",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Update profile section (CRM sync)",
    description:
      "Updates one section of the profile. Pushes contact_queue.updateContact to sync with CRM. FST-blocked.",
    fstBlocked: true,
    consumers: {
      mobile: [
        "api/user.js > updateProfile",
        "api/user.js > updateUSerProfile (legacy alias)",
      ],
      web: [],
    },
    controller: "userAdditionalController.updateUserProfile",
  },
  {
    id: "users-referral",
    method: "POST",
    path: "/users/referral",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Get referral code",
    description:
      "Read-only POST → allowed inside FST sandbox. Returns the user's referral code.",
    consumers: {
      mobile: ["api/user.js > fetchReferralCode (Invite / referrals screens)"],
      web: [],
    },
    controller: "userController.getReferralCodeMiddleWare",
  },
  {
    id: "users-region",
    method: "GET",
    path: "/users/region",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Get language & region",
    description: "Returns the user's current language and region.",
    consumers: { mobile: ["api/region.js > getRegion"], web: [] },
    controller: "userController.getLanRegionMiddleWare",
  },
  {
    id: "users-lan",
    method: "POST",
    path: "/users/lan",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Update language & region",
    description: "Updates the user's preferred language + region. FST-blocked.",
    fstBlocked: true,
    consumers: {
      mobile: ["api/user.js > userLanguages", "api/region.js > updateRegion"],
      web: [],
    },
    controller: "userController.updateLanRegionMiddleware",
  },
  {
    id: "users-reports",
    method: "GET",
    path: "/users/reports",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Power BI report ids",
    description:
      "Returns the Power BI report ids used by the embedded reports screen.",
    consumers: {
      mobile: ["api/user.js > getRptIds (screens/reports/*)"],
      web: [],
    },
    controller: "userController.getFeReportIdsMiddleWare",
  },

  // ───────── OTPs ─────────
  {
    id: "otps-email",
    method: "POST",
    path: "/userOtps/",
    category: "OTPs",
    auth: "Public",
    title: "Email OTP",
    description: "Generates an email-based OTP (signup, forgot password).",
    fstBlocked: true,
    consumers: { mobile: ["api/user.js > sendOtp"], web: [] },
    controller: "userOtpControllers",
  },
  {
    id: "otps-mobile",
    method: "POST",
    path: "/userOtps/mobile",
    category: "OTPs",
    auth: "Public",
    title: "Mobile OTP (Twilio)",
    description: "Sends a mobile OTP via Twilio Verify.",
    consumers: { mobile: [], web: [] },
    controller: "userOtpControllers",
  },
  {
    id: "otps-send",
    method: "POST",
    path: "/userOtps/send",
    category: "OTPs",
    auth: "broker|user",
    title: "Resend OTP (authenticated)",
    description: "Re-sends an OTP for sensitive flows (e.g. change mobile).",
    fstBlocked: true,
    consumers: {
      mobile: ["api/user.js > MobileVerify", "api/user.js > sendOTP (alias)"],
      web: [],
    },
    controller: "userOtpControllers",
  },
  {
    id: "otps-validate",
    method: "POST",
    path: "/userOtps/validate",
    category: "OTPs",
    auth: "broker|user",
    title: "Validate OTP (authenticated)",
    description: "Validates an OTP for a sensitive action.",
    fstBlocked: true,
    consumers: { mobile: ["api/user.js > validateMobileOTP"], web: [] },
    controller: "userOtpControllers",
  },

  // ───────── Nationalities & Occupations ─────────
  {
    id: "nationalities",
    method: "GET",
    path: "/nationalities/:id?",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Nationalities list",
    consumers: { mobile: ["api/user.js > getNationalities"], web: [] },
    controller: "nationalitiesController",
  },
  {
    id: "occupations",
    method: "GET",
    path: "/occupations/:id?",
    category: "Profile & Lookups",
    auth: "broker|user",
    title: "Occupations list",
    consumers: { mobile: ["api/user.js > getOccupations"], web: [] },
    controller: "occupationController",
  },

  // ───────── Subscriptions ─────────
  {
    id: "subs-list",
    method: "GET",
    path: "/subscriptions",
    category: "Subscriptions",
    auth: "user",
    title: "List my subscriptions",
    description:
      "Returns subscriptions for the logged-in user (or admin grid filtered by name/introducerId/amount/shareClassType).",
    consumers: {
      mobile: [],
      web: ["src/apis/subscriptionsApis.js > getSubscriptions"],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-export",
    method: "GET",
    path: "/subscriptions/export",
    category: "Subscriptions",
    auth: "JWT",
    title: "Export subscriptions (XLSX/CSV)",
    consumers: { mobile: [], web: [] },
    controller: "subscriptionController",
  },
  {
    id: "subs-broker",
    method: "GET",
    path: "/subscriptions/broker",
    category: "Subscriptions",
    auth: "broker",
    title: "Broker's subscriptions",
    consumers: {
      mobile: [
        "api/subscriptionApis.js > getSubscriptionList",
        "api/subscriptionApis.js > getSubscriptionsList",
      ],
      web: [],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-broker-stats",
    method: "GET",
    path: "/subscriptions/broker/stats/:type?",
    category: "Subscriptions",
    auth: "broker",
    title: "Broker portfolio stats",
    description:
      "Portfolio stats for the broker. ⚠ Conflicts with /subscriptions/broker/stats/:id route — the second route is unreachable as written.",
    consumers: {
      mobile: [
        "api/subscriptionApis.js > brokerStats",
        "api/subscribersApi.js > getAll (⚠ buggy — type is undefined)",
      ],
      web: ["src/apis/usersApis.js > getBrokerPortfolio (uses :id form)"],
    },
    controller: "subscriptionController",
    notes: [
      "Route ordering bug: brokerStats/:type? declared before /broker/stats/:id",
    ],
  },
  {
    id: "subs-broker-stats-id",
    method: "GET",
    path: "/subscriptions/broker/stats/:id",
    category: "Subscriptions",
    auth: "JWT",
    title: "Broker portfolio by broker id (admin)",
    description:
      "⚠ Currently unreachable — shadowed by /broker/stats/:type? declared before it.",
    consumers: {
      mobile: [],
      web: ["src/apis/usersApis.js > getBrokerPortfolio (intended)"],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-investors-stats",
    method: "GET",
    path: "/subscriptions/investors/stats/",
    category: "Subscriptions",
    auth: "JWT",
    title: "Investor portfolio stats (admin)",
    consumers: {
      mobile: [],
      web: ["src/apis/usersApis.js > getInvestorPortfolio"],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-investor-list",
    method: "GET",
    path: "/subscriptions/investor/list/:type?",
    category: "Subscriptions",
    auth: "broker|user",
    title: "Investor's subscription list",
    consumers: {
      mobile: [
        "api/subscriptionApis.js > getInvestmentList (type=investments)",
      ],
      web: [],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-investor-product",
    method: "GET",
    path: "/subscriptions/investor/product",
    category: "Subscriptions",
    auth: "broker|user",
    title: "Investor product group",
    description: "Group subscriptions by product (used in invest flow).",
    consumers: {
      mobile: ["api/subscriptionApis.js > getInvestorProduct"],
      web: [],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-investor-latest",
    method: "GET",
    path: "/subscriptions/investor/latest",
    category: "Subscriptions",
    auth: "broker|user",
    title: "Latest subscription",
    description:
      "Returns the latest subscription (used during onboarding/resume).",
    consumers: {
      mobile: ["api/subscriptionApis.js > getInvestorInfo"],
      web: [],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-investor-documents",
    method: "GET",
    path: "/subscriptions/investor/documents",
    category: "Subscriptions",
    auth: "broker|user",
    title: "Investor's documents",
    description:
      "All documents (DocuSeal signed PDFs, receipts) for the investor under a product.",
    consumers: {
      mobile: ["api/subscriptionApis.js > getInvestorDocuments"],
      web: [],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-investor-id",
    method: "GET",
    path: "/subscriptions/investor/:id",
    category: "Subscriptions",
    auth: "JWT",
    title: "Single subscription detail",
    consumers: {
      mobile: ["api/interest.js > getSourceOfFounds"],
      web: [],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-id",
    method: "GET",
    path: "/subscriptions/:id",
    category: "Subscriptions",
    auth: "broker",
    title: "Subscription by id",
    consumers: {
      mobile: ["api/subscriptionApis.js > getSubscriptionDetails"],
      web: ["src/apis/subscriptionsApis.js > getSubscriptionDetails"],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-create",
    method: "POST",
    path: "/subscriptions/:id?",
    category: "Subscriptions",
    auth: "broker",
    title: "Create / update subscription",
    description:
      "Broker creates or updates a subscription on behalf of an investor. Creates CRM opportunity + investment records.",
    fstBlocked: true,
    consumers: {
      mobile: [
        "api/subscription.js > addSubscription (screens/invest/investNow/reviewOfInv.js)",
      ],
      web: [],
    },
    controller: "subscriptionController.createSubscription",
  },
  {
    id: "subs-investor-post",
    method: "POST",
    path: "/subscriptions/investor/:id?",
    category: "Subscriptions",
    auth: "JWT",
    title: "Investor create/update subscription",
    consumers: { mobile: ["used by invest flow"], web: [] },
    controller: "subscriptionController",
  },
  {
    id: "subs-signRequest",
    method: "POST",
    path: "/subscriptions/signRequest",
    category: "Subscriptions",
    auth: "JWT",
    title: "Trigger DocuSeal signature",
    description:
      "Calls lib/docuseal.createSubmission with >40 prefilled fields. Pushes docuseal_queue.complete with 200s delay.",
    fstBlocked: true,
    consumers: {
      mobile: [
        "api/subscriptionApis.js > sendSignRequest (screens/invest/investNow/reviewOfInv.js)",
      ],
      web: [],
    },
    controller: "subscriptionController",
  },
  {
    id: "subs-payment",
    method: "PUT",
    path: "/subscriptions/payment/:id",
    category: "Subscriptions",
    auth: "JWT",
    title: "Update subscription payment status",
    consumers: { mobile: [], web: [] },
    controller: "subscriptionController",
  },
  {
    id: "subs-broker-investor-stats",
    method: "GET",
    path: "/subscriptions/broker/investor/stats/:id/:type?",
    category: "Subscriptions",
    auth: "broker",
    title: "Stats for one investor under a broker",
    consumers: { mobile: [], web: [] },
    controller: "subscriptionController",
  },

  // ───────── Subscribers ─────────
  {
    id: "subscribers-list",
    method: "GET",
    path: "/subscribers/",
    category: "Subscribers",
    auth: "broker|user",
    title: "List subscribers",
    consumers: {
      mobile: [
        "api/subscribersApi.js > getAllsubscribers",
        "api/subscribersApi.js > getRecordDetails",
      ],
      web: [],
    },
    controller: "subscriberController",
  },
  {
    id: "subscribers-home",
    method: "GET",
    path: "/subscribers/home",
    category: "Subscribers",
    auth: "broker|user",
    title: "Home-screen aggregate",
    consumers: {
      mobile: ["api/subscribersApi.js > getUserWealth (Home dashboard widget)"],
      web: [],
    },
    controller: "subscriberController",
  },
  {
    id: "subscribers-upsert",
    method: "POST",
    path: "/subscribers/:id?",
    category: "Subscribers",
    auth: "broker",
    title: "Create / update subscriber",
    fstBlocked: true,
    consumers: {
      mobile: ["api/subscribersApi.js > upsert (Broker → Add/Edit subscriber)"],
      web: [],
    },
    controller: "subscriberController",
  },

  // ───────── Redemptions ─────────
  {
    id: "redemptions-create",
    method: "POST",
    path: "/redemptions/:id?",
    category: "Redemptions",
    auth: "broker|user",
    title: "Create / update redemption",
    description:
      "Kicks off the DocuSeal redemption form, creates a CRM case via crmCase_queue, sends confirmation email.",
    fstBlocked: true,
    consumers: {
      mobile: [
        "api/subscriptionApis.js > insertReedmption (screens/invest/investments/RedeemInvScreen.js)",
      ],
      web: [],
    },
    controller: "redemptionController.upsertRedemptions",
  },
  {
    id: "redemptions-id",
    method: "GET",
    path: "/redemptions/:id",
    category: "Redemptions",
    auth: "broker|user",
    title: "Redemption detail",
    description:
      "⚠ Route ordering bug — declared before /redemptions/investor which makes the latter currently unreachable.",
    consumers: {
      mobile: ["api/subscriptionApis.js > getReedmptionInfo"],
      web: [],
    },
    controller: "redemptionController",
  },
  {
    id: "redemptions-investor",
    method: "GET",
    path: "/redemptions/investor",
    category: "Redemptions",
    auth: "JWT",
    title: "Investor's redemptions",
    description:
      "⚠ Currently unreachable due to route ordering (see /redemptions/:id).",
    consumers: { mobile: [], web: [] },
    controller: "redemptionController",
  },
  {
    id: "redemptions-list",
    method: "GET",
    path: "/redemptions/list",
    category: "Redemptions",
    auth: "JWT",
    title: "Redemptions list (admin)",
    consumers: {
      mobile: [],
      web: ["src/apis/Redemptions.js > getRedemptionsServer"],
    },
    controller: "redemptionController",
  },

  // ───────── Applications ─────────
  {
    id: "apps-list",
    method: "GET",
    path: "/applications",
    category: "Investments / Applications",
    auth: "JWT",
    title: "Applications for current broker",
    consumers: { mobile: [], web: [] },
    controller: "applicationController",
  },
  {
    id: "apps-all",
    method: "GET",
    path: "/applications/all",
    category: "Investments / Applications",
    auth: "JWT (admin)",
    title: "All applications (admin)",
    consumers: {
      mobile: [],
      web: ["src/apis/applicationsApi.js > getApplications"],
    },
    controller: "applicationController",
  },
  {
    id: "apps-leads",
    method: "GET",
    path: "/applications/leads",
    category: "Investments / Applications",
    auth: "JWT",
    title: "Broker's lead pipeline",
    consumers: { mobile: [], web: [] },
    controller: "applicationController",
  },
  {
    id: "apps-submit",
    method: "GET",
    path: "/applications/submit",
    category: "Investments / Applications",
    auth: "JWT",
    title: "Submitted applications",
    consumers: {
      mobile: ["api/subscriptionApis.js > getInvestmentsList"],
      web: [],
    },
    controller: "applicationController",
  },
  {
    id: "apps-submit-all",
    method: "GET",
    path: "/applications/submit/all",
    category: "Investments / Applications",
    auth: "JWT (admin)",
    title: "All submitted applications (admin)",
    consumers: {
      mobile: [],
      web: ["src/apis/investmentsApi.js > getInvestments"],
    },
    controller: "applicationController",
  },
  {
    id: "apps-create",
    method: "POST",
    path: "/applications/",
    category: "Investments / Applications",
    auth: "JWT",
    title: "Create application",
    consumers: { mobile: [], web: [] },
    controller: "applicationController",
  },
  {
    id: "apps-update",
    method: "PUT",
    path: "/applications/:id",
    category: "Investments / Applications",
    auth: "JWT",
    title: "Update application",
    consumers: { mobile: [], web: [] },
    controller: "applicationController",
  },
  {
    id: "apps-detail",
    method: "GET",
    path: "/applications/:id",
    category: "Investments / Applications",
    auth: "JWT",
    title: "Application detail",
    consumers: {
      mobile: [],
      web: [
        "src/apis/applicationsApi.js > getApplicationsDetails",
        "src/apis/investmentsApi.js > getInvestmentsDetails",
      ],
    },
    controller: "applicationController",
  },

  // ───────── Products ─────────
  {
    id: "products-list",
    method: "GET",
    path: "/products/",
    category: "Products & Catalog",
    auth: "JWT",
    title: "List products",
    consumers: { mobile: ["api/shareClass.js > getShareClassList"], web: [] },
    controller: "productController",
  },
  {
    id: "products-list-web",
    method: "GET",
    path: "/products/list",
    category: "Products & Catalog",
    auth: "JWT",
    title: "Web-facing products list",
    consumers: {
      mobile: [],
      web: [
        "src/apis/productApis.js > getProductList",
        "src/apis/shareClassApi.js > getShareClass",
      ],
    },
    controller: "productController",
  },
  {
    id: "products-create",
    method: "POST",
    path: "/products/",
    category: "Products & Catalog",
    auth: "JWT",
    title: "Create product",
    consumers: {
      mobile: [],
      web: [
        "src/apis/productApis.js > createProduct",
        "src/apis/shareClassApi.js > createShareClass",
      ],
    },
    controller: "productController",
  },
  {
    id: "products-cat",
    method: "GET",
    path: "/products/:catId",
    category: "Products & Catalog",
    auth: "JWT",
    title: "Products by category id",
    consumers: { mobile: [], web: [] },
    controller: "productController",
  },
  {
    id: "products-update",
    method: "PUT",
    path: "/products/:id",
    category: "Products & Catalog",
    auth: "JWT",
    title: "Update product / share class",
    consumers: {
      mobile: [],
      web: [
        "src/apis/productApis.js > updateProduct",
        "src/apis/shareClassApi.js > updateShareClass",
      ],
    },
    controller: "productController",
  },
  {
    id: "products-delete",
    method: "DELETE",
    path: "/products/:id",
    category: "Products & Catalog",
    auth: "JWT",
    title: "Delete product / share class",
    consumers: {
      mobile: [],
      web: ["src/apis/shareClassApi.js > deleteShareClass"],
    },
    controller: "productController",
  },
  {
    id: "products-detail",
    method: "GET",
    path: "/products/:id",
    category: "Products & Catalog",
    auth: "JWT",
    title: "Product detail",
    consumers: {
      mobile: [],
      web: ["src/apis/productApis.js > getProductinfoServer"],
    },
    controller: "productController",
  },

  // ───────── Product Performance ─────────
  {
    id: "pp-upsert",
    method: "POST",
    path: "/productPerformance/",
    category: "Product Performance",
    auth: "JWT",
    title: "Upsert performance row",
    consumers: {
      mobile: [],
      web: ["src/apis/ProductPerformanceApi.js > createProductPerformance"],
    },
    controller: "productPerformanceController",
  },
  {
    id: "pp-list",
    method: "GET",
    path: "/productPerformance/",
    category: "Product Performance",
    auth: "broker|user",
    title: "Performance list",
    consumers: {
      mobile: ["api/products.js > getProductPerformance"],
      web: ["src/apis/ProductPerformanceApi.js > getProductPerformance"],
    },
    controller: "productPerformanceController",
  },
  {
    id: "pp-crm-product",
    method: "GET",
    path: "/productPerformance/crm-product",
    category: "Product Performance",
    auth: "JWT",
    title: "Lookup by crmProductId",
    consumers: { mobile: [], web: [] },
    controller: "productPerformanceController",
  },
  {
    id: "pp-share-class",
    method: "GET",
    path: "/productPerformance/share-class/:crmShareClassGuid",
    category: "Product Performance",
    auth: "JWT",
    title: "Lookup by share-class guid",
    consumers: { mobile: [], web: [] },
    controller: "productPerformanceController",
  },
  {
    id: "pp-detail",
    method: "GET",
    path: "/productPerformance/:id",
    category: "Product Performance",
    auth: "JWT",
    title: "Performance detail",
    consumers: {
      mobile: [],
      web: ["src/apis/ProductPerformanceApi.js > getProductPerformance"],
    },
    controller: "productPerformanceController",
  },
  {
    id: "pp-update",
    method: "PUT",
    path: "/productPerformance/:id",
    category: "Product Performance",
    auth: "JWT",
    title: "Update performance row",
    consumers: {
      mobile: [],
      web: ["src/apis/ProductPerformanceApi.js > updateProductPerformance"],
    },
    controller: "productPerformanceController",
  },
  {
    id: "pp-delete",
    method: "DELETE",
    path: "/productPerformance/:id",
    category: "Product Performance",
    auth: "JWT",
    title: "Delete performance row",
    consumers: {
      mobile: [],
      web: ["src/apis/ProductPerformanceApi.js > deleteProductPerformance"],
    },
    controller: "productPerformanceController",
  },

  // ───────── Categories ─────────
  {
    id: "categories-get",
    method: "GET",
    path: "/categories/:id?",
    category: "Categories",
    auth: "Public",
    title: "Categories list/get",
    consumers: {
      mobile: ["api/products.js > getCategories", "api/categories.js > getAll"],
      web: [
        "src/apis/AppProductsApi.js > getAppProducts",
        "src/apis/categoriesApis.js > getCategoreis",
      ],
    },
    controller: "categoriesController",
  },
  {
    id: "categories-upsert",
    method: "POST",
    path: "/categories/:id?",
    category: "Categories",
    auth: "JWT",
    title: "Upsert category (App Product)",
    description:
      "Web admin uses POST (not PUT) for updates — matches backend convention.",
    consumers: {
      mobile: [],
      web: [
        "src/apis/AppProductsApi.js > createAppProducts",
        "src/apis/AppProductsApi.js > updateAppProducts",
      ],
    },
    controller: "categoriesController",
  },
  {
    id: "categories-delete",
    method: "DELETE",
    path: "/categories/:id",
    category: "Categories",
    auth: "JWT",
    title: "Delete category",
    consumers: {
      mobile: [],
      web: ["src/apis/AppProductsApi.js > deleteAppProducts"],
    },
    controller: "categoriesController",
  },

  // ───────── News ─────────
  {
    id: "news-list",
    method: "GET",
    path: "/news/:id?",
    category: "News",
    auth: "Public",
    title: "List / get news",
    description:
      "Mix of DB rows and RSS feed (lib/news.js scrapes mynewsdesk.com).",
    consumers: {
      mobile: ["api/news.js > getAllNews"],
      web: ["src/apis/newsApi.js > newsServer"],
    },
    controller: "newsController",
  },
  {
    id: "news-upload",
    method: "POST",
    path: "/news/upload",
    category: "News",
    auth: "Public",
    title: "Upload image to Azure Blob",
    consumers: {
      mobile: ["api/news.js > uploadImage"],
      web: ["src/apis/newsApi.js > uploadImageServer"],
    },
    controller: "newsController / imageController",
  },
  {
    id: "news-create",
    method: "POST",
    path: "/news/",
    category: "News",
    auth: "JWT",
    title: "Create article",
    consumers: { mobile: [], web: ["src/apis/newsApi.js > createNewsServer"] },
    controller: "newsController",
  },
  {
    id: "news-update",
    method: "PUT",
    path: "/news/:id",
    category: "News",
    auth: "JWT",
    title: "Update article",
    consumers: { mobile: [], web: ["src/apis/newsApi.js > updateNewsServer"] },
    controller: "newsController",
  },
  {
    id: "news-like",
    method: "POST",
    path: "/news/like",
    category: "News",
    auth: "JWT",
    title: "Like / unlike article",
    consumers: { mobile: [], web: [] },
    controller: "newsController",
  },
  {
    id: "news-delete",
    method: "DELETE",
    path: "/news/:id",
    category: "News",
    auth: "JWT",
    title: "Delete article",
    consumers: { mobile: [], web: ["src/apis/newsApi.js > deleteNewsServer"] },
    controller: "newsController",
  },

  // ───────── Configurations ─────────
  {
    id: "config-get",
    method: "GET",
    path: "/configurations/:id?",
    category: "Configurations",
    auth: "Public",
    title: "Read config",
    consumers: {
      mobile: ["api/news.js > getNaves (NAV widget, cached in SQLite)"],
      web: ["src/apis/ConfigurationsApi.js > getConfigurations"],
    },
    controller: "configurationController",
  },
  {
    id: "config-update",
    method: "PUT",
    path: "/configurations/:id",
    category: "Configurations",
    auth: "JWT (admin)",
    title: "Update config",
    consumers: {
      mobile: [],
      web: ["src/apis/ConfigurationsApi.js > updateConfigurations"],
    },
    controller: "configurationController",
  },

  // ───────── FAQs ─────────
  {
    id: "faqs-create",
    method: "POST",
    path: "/faqs/",
    category: "FAQs",
    auth: "JWT",
    title: "Create FAQ",
    consumers: { mobile: [], web: ["src/apis/faqsApi.js > createFaqs"] },
    controller: "faqsController",
  },
  {
    id: "faqs-image",
    method: "POST",
    path: "/faqs/image",
    category: "FAQs",
    auth: "JWT",
    title: "Upload FAQ image",
    consumers: { mobile: [], web: [] },
    controller: "faqsController",
  },
  {
    id: "faqs-update",
    method: "PUT",
    path: "/faqs/:id",
    category: "FAQs",
    auth: "JWT",
    title: "Update FAQ",
    consumers: { mobile: [], web: ["src/apis/faqsApi.js > updateFaqs"] },
    controller: "faqsController",
  },
  {
    id: "faqs-get",
    method: "GET",
    path: "/faqs/:id?",
    category: "FAQs",
    auth: "broker|user",
    title: "List / get FAQs",
    consumers: {
      mobile: ["api/faqs.js > getFaqs"],
      web: ["src/apis/faqsApi.js > getFaqs"],
    },
    controller: "faqsController",
  },
  {
    id: "faqs-delete",
    method: "DELETE",
    path: "/faqs/:id",
    category: "FAQs",
    auth: "JWT",
    title: "Delete FAQ",
    consumers: { mobile: [], web: ["src/apis/faqsApi.js > deleteFaqs"] },
    controller: "faqsController",
  },

  // ───────── Documents ─────────
  {
    id: "docs-get",
    method: "GET",
    path: "/documents/:id?",
    category: "Documents",
    auth: "broker|user",
    title: "List / get product documents",
    consumers: {
      mobile: ["api/support.js > getAllDocUrls"],
      web: [
        "src/apis/documentApi.js > getDocument",
        "src/apis/AllDocuments.js > getDocumentsDynamic (⚠ duplicate of documentApi.js)",
      ],
    },
    controller: "documentController",
  },
  {
    id: "docs-create",
    method: "POST",
    path: "/documents/",
    category: "Documents",
    auth: "JWT",
    title: "Create document",
    consumers: {
      mobile: [],
      web: ["src/apis/documentApi.js > createDocument"],
    },
    controller: "documentController",
  },
  {
    id: "docs-update",
    method: "PUT",
    path: "/documents/:id",
    category: "Documents",
    auth: "JWT",
    title: "Update document",
    consumers: {
      mobile: [],
      web: ["src/apis/documentApi.js > updateDocument"],
    },
    controller: "documentController",
  },
  {
    id: "docs-delete",
    method: "DELETE",
    path: "/documents/:id",
    category: "Documents",
    auth: "JWT",
    title: "Delete document",
    consumers: {
      mobile: [],
      web: ["src/apis/documentApi.js > deleteDocument"],
    },
    controller: "documentController",
  },
  {
    id: "gmdocs-get",
    method: "GET",
    path: "/gmDocuments/:id?",
    category: "Documents",
    auth: "JWT",
    title: "GM corporate documents",
    consumers: {
      mobile: ["api/support.js > getDocUrls"],
      web: [],
    },
    controller: "gmDocumentsController",
  },
  {
    id: "gmdocs-create",
    method: "POST",
    path: "/gmDocuments/",
    category: "Documents",
    auth: "JWT",
    title: "Create GM document",
    consumers: { mobile: [], web: [] },
    controller: "gmDocumentsController",
  },
  {
    id: "gmdocs-update",
    method: "PUT",
    path: "/gmDocuments/:id",
    category: "Documents",
    auth: "JWT",
    title: "Update GM document",
    consumers: { mobile: [], web: [] },
    controller: "gmDocumentsController",
  },
  {
    id: "gmdocs-delete",
    method: "DELETE",
    path: "/gmDocuments/:id",
    category: "Documents",
    auth: "JWT",
    title: "Delete GM document",
    consumers: { mobile: [], web: [] },
    controller: "gmDocumentsController",
  },

  // ───────── Notifications ─────────
  {
    id: "notif-tokens",
    method: "GET",
    path: "/notifications/",
    category: "Notifications",
    auth: "broker|user",
    title: "Tokens list (admin preview)",
    consumers: { mobile: [], web: [] },
    controller: "notificationsController",
  },
  {
    id: "notif-list",
    method: "GET",
    path: "/notifications/List",
    category: "Notifications",
    auth: "JWT",
    title: "All notifications (admin)",
    description:
      "Returns the notifications grid; pass ?id= to fetch detail + recipients.",
    consumers: {
      mobile: [],
      web: ["src/apis/notificationApi.js > getNotificationList"],
    },
    controller: "notificationsController",
  },
  {
    id: "notif-search",
    method: "GET",
    path: "/notification",
    category: "Notifications",
    auth: "JWT",
    title: "Recipient search",
    consumers: {
      mobile: [],
      web: ["src/apis/notificationApi.js > getNotifications"],
    },
    controller: "notificationsController",
  },
  {
    id: "notif-send",
    method: "POST",
    path: "/notifications/",
    category: "Notifications",
    auth: "JWT",
    title: "Send push notification",
    description:
      "Resolves recipients, creates notifications row (3 rows when multilang EN/DE/PL), enqueues notification_queue.sendNotification (with optional delay), bulk-inserts userNotifications, emits unread_count to each user socket.",
    consumers: {
      mobile: [],
      web: ["src/apis/notificationApi.js > sendNotications"],
    },
    controller: "notificationsController.sendPushNotifications",
  },
  {
    id: "notif-users",
    method: "GET",
    path: "/notifications/users/:id?",
    category: "Notifications",
    auth: "broker|user",
    title: "Notifications for current user",
    description: "Paginated list of notifications for the logged-in user.",
    consumers: {
      mobile: ["api/notifications.js > getNotificationList"],
      web: [],
    },
    controller: "notificationsController",
  },
  {
    id: "notif-read",
    method: "PUT",
    path: "/notifications/",
    category: "Notifications",
    auth: "broker|user",
    title: "Mark notification read",
    description:
      "Marks notification(s) as read and emits an updated unread_count to the user's socket room.",
    fstBlocked: true,
    consumers: {
      mobile: ["api/notifications.js > changeNotificationStatus"],
      web: [],
    },
    controller: "notificationsController",
  },
  {
    id: "notif-sent",
    method: "GET",
    path: "/notifications/getSentNotifications",
    category: "Notifications",
    auth: "JWT",
    title: "Sent notifications dashboard",
    consumers: {
      mobile: [],
      web: ["src/apis/SentNotificationsApi.js > getSentNotifications"],
    },
    controller: "notificationsController",
  },
  {
    id: "notif-export",
    method: "GET",
    path: "/notifications/getExportNotifyAdmin",
    category: "Notifications",
    auth: "JWT",
    title: "Export sent notifications",
    consumers: {
      mobile: [],
      web: [
        "src/apis/SentNotificationsApi.js > getExportsListDataForNotifications",
      ],
    },
    controller: "notificationsController",
  },
  {
    id: "notif-delete",
    method: "DELETE",
    path: "/notifications/:id",
    category: "Notifications",
    auth: "JWT",
    title: "Soft-delete notification",
    consumers: { mobile: [], web: [] },
    controller: "notificationsController",
  },

  // ───────── Notification Settings ─────────
  {
    id: "notifset-get",
    method: "GET",
    path: "/notifiSettings/",
    category: "Notification Settings",
    auth: "broker|user",
    title: "Get notification toggles",
    consumers: {
      mobile: ["api/notifications.js > GetNotificationStatus"],
      web: [],
    },
    controller: "notificationSettingsController",
  },
  {
    id: "notifset-put",
    method: "PUT",
    path: "/notifiSettings/",
    category: "Notification Settings",
    auth: "broker|user",
    title: "Update notification toggles",
    fstBlocked: true,
    consumers: {
      mobile: ["api/notifications.js > postNotificationStatus"],
      web: [],
    },
    controller: "notificationSettingsController",
  },

  // ───────── Promotions / Offers ─────────
  {
    id: "promo-all-locs",
    method: "POST",
    path: "/promotions/allLocations",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "All offer locations (read-only POST, FST allowed)",
    consumers: { mobile: ["api/promotions.js > getAllLocations"], web: [] },
    controller: "promotionsController",
  },
  {
    id: "promo-get-loc",
    method: "POST",
    path: "/promotions/getLoc",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Locations filtered by coords (read-only POST)",
    consumers: { mobile: [], web: [] },
    controller: "promotionsController",
  },
  {
    id: "promo-addFav",
    method: "POST",
    path: "/promotions/addFav",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Add favourite",
    consumers: { mobile: ["api/promotions.js > addFavourites"], web: [] },
    controller: "promotionsController",
  },
  {
    id: "promo-cats",
    method: "POST",
    path: "/promotions/categories/:id?",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Upsert offer category",
    consumers: {
      mobile: [],
      web: [
        "src/apis/categoryApi.js > createCategoriesServer",
        "src/apis/categoryApi.js > updateCategoriesServer",
      ],
    },
    controller: "promotionsController",
  },
  {
    id: "promo-cats-soft-delete",
    method: "PUT",
    path: "/promotions/categories/:id",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Soft-delete category",
    consumers: {
      mobile: [],
      web: ["src/apis/categoryApi.js > deleteCategoriesServer"],
    },
    controller: "promotionsController",
  },
  {
    id: "promo-offers",
    method: "POST",
    path: "/promotions/offers/:id?",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Upsert offer",
    consumers: {
      mobile: [],
      web: ["src/apis/offersApi.js > createAndUpdateOffersServer"],
    },
    controller: "promotionsController",
  },
  {
    id: "promo-loc-upsert",
    method: "POST",
    path: "/promotions/:id?",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Upsert location",
    consumers: {
      mobile: [],
      web: ["src/apis/locationApi.js > createAndUpdateLocationsServer"],
    },
    controller: "promotionsController",
  },
  {
    id: "promo-get-offers",
    method: "GET",
    path: "/promotions/getOffers/:id?",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Offers list / detail",
    consumers: { mobile: [], web: ["src/apis/offersApi.js > getOffersServer"] },
    controller: "promotionsController",
  },
  {
    id: "promo-get-cats",
    method: "GET",
    path: "/promotions/getCategories/:id?",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Promo categories list",
    consumers: {
      mobile: ["api/promotions.js > getAllCategories (⚠ implicit-global bug)"],
      web: ["src/apis/categoryApi.js > getCategoriesServer"],
    },
    controller: "promotionsController",
  },
  {
    id: "promo-get-locs",
    method: "GET",
    path: "/promotions/getLocations/:id?",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Locations list (admin)",
    consumers: {
      mobile: [],
      web: ["src/apis/locationApi.js > getLocationsServer"],
    },
    controller: "promotionsController",
  },
  {
    id: "promo-fav-delete",
    method: "DELETE",
    path: "/promotions/:id",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Remove favourite",
    consumers: { mobile: ["api/promotions.js > delFavourites"], web: [] },
    controller: "promotionsController",
  },
  {
    id: "promo-loc-soft-delete",
    method: "PUT",
    path: "/promotions/:id",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Soft-delete location",
    consumers: {
      mobile: [],
      web: ["src/apis/locationApi.js > deleteLocationsServer"],
    },
    controller: "promotionsController",
  },
  {
    id: "promo-loc-detail",
    method: "GET",
    path: "/promotions/:id",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Location with offers",
    consumers: { mobile: ["api/promotions.js > getOffers"], web: [] },
    controller: "promotionsController",
  },
  {
    id: "offers-update",
    method: "PUT",
    path: "/offers/:id",
    category: "Promotions / Offers",
    auth: "JWT",
    title: "Update / soft-delete offer",
    consumers: {
      mobile: [],
      web: [
        "src/apis/offersApi.js > updateOffersServer",
        "src/apis/offersApi.js > deleteOffersServer (soft delete via PUT)",
      ],
    },
    controller: "promotionsController",
  },

  // ───────── Rewards / Coins ─────────
  {
    id: "rewards-user",
    method: "GET",
    path: "/rewards/user",
    category: "Rewards / Coins",
    auth: "broker|user",
    title: "My rewards balance",
    consumers: { mobile: ["api/user.js > referralPoints"], web: [] },
    controller: "rewardsController",
  },
  {
    id: "rewards-list",
    method: "GET",
    path: "/rewards/:id?",
    category: "Rewards / Coins",
    auth: "admin",
    title: "List / get rewards",
    consumers: {
      mobile: [],
      web: ["src/apis/pointsApis.js > getPointsServer"],
    },
    controller: "rewardsController",
  },
  {
    id: "rewards-upsert",
    method: "POST",
    path: "/rewards/:id?",
    category: "Rewards / Coins",
    auth: "admin",
    title: "Upsert reward",
    consumers: {
      mobile: [],
      web: ["src/apis/pointsApis.js > createAndUpdatePointsServer"],
    },
    controller: "rewardsController",
  },
  {
    id: "rewards-delete",
    method: "PUT",
    path: "/rewards/:id",
    category: "Rewards / Coins",
    auth: "admin",
    title: "Soft-delete reward",
    consumers: {
      mobile: [],
      web: ["src/apis/pointsApis.js > deletePointsServer"],
    },
    controller: "rewardsController",
  },
  {
    id: "points-update",
    method: "PUT",
    path: "/points/:id",
    category: "Rewards / Coins",
    auth: "admin",
    title: "Update points",
    consumers: {
      mobile: [],
      web: ["src/apis/pointsApis.js > updatePointsServer"],
    },
    controller: "rewardsController",
  },

  // ───────── Dates For Your Diary ─────────
  {
    id: "dates-list",
    method: "GET",
    path: "/dates/datesList",
    category: "Dates For Your Diary",
    auth: "JWT",
    title: "Diary list (non-admin)",
    consumers: {
      mobile: ["api/datesForDairy.js > getCategoriesList"],
      web: [],
    },
    controller: "datesController",
  },
  {
    id: "dates-admin-list",
    method: "GET",
    path: "/dates/:id?",
    category: "Dates For Your Diary",
    auth: "admin",
    title: "Diary list (admin)",
    consumers: {
      mobile: [],
      web: ["src/apis/DatesForDairiesApi.js > getDairiesServer"],
    },
    controller: "datesController",
  },
  {
    id: "dates-upsert",
    method: "POST",
    path: "/dates/:id?",
    category: "Dates For Your Diary",
    auth: "admin",
    title: "Upsert diary entry",
    consumers: {
      mobile: [],
      web: ["src/apis/DatesForDairiesApi.js > createAndUpdateDairiesServer"],
    },
    controller: "datesController",
  },
  {
    id: "dates-update",
    method: "PUT",
    path: "/dates/:id",
    category: "Dates For Your Diary",
    auth: "admin",
    title: "Update / soft-delete diary entry",
    consumers: {
      mobile: [],
      web: [
        "src/apis/DatesForDairiesApi.js > updateDairiesServer",
        "src/apis/DatesForDairiesApi.js > deleteDairiesServer",
      ],
    },
    controller: "datesController",
  },

  // ───────── Support / Tickets ─────────
  {
    id: "tickets-history",
    method: "GET",
    path: "/tickets/history/:id",
    category: "Support / Tickets",
    auth: "JWT",
    title: "Ticket message history",
    consumers: { mobile: [], web: ["inline in getInTouchDetails.js"] },
    controller: "supportController",
  },
  {
    id: "tickets-create",
    method: "POST",
    path: "/tickets/",
    category: "Support / Tickets",
    auth: "broker|user",
    title: "Create support ticket",
    description:
      "Pushes a CRM Case via crmCase_queue and sends a confirmation email through Azure ACS.",
    fstBlocked: true,
    consumers: {
      mobile: ["api/contactUs.js > contactUs (type: ContactUs / feedBack)"],
      web: [],
    },
    controller: "supportController.createTicket",
  },
  {
    id: "tickets-update",
    method: "PUT",
    path: "/tickets/:id",
    category: "Support / Tickets",
    auth: "JWT",
    title: "Update ticket status",
    consumers: { mobile: [], web: ["inline in getInTouchDetails.js"] },
    controller: "supportController",
  },
  {
    id: "tickets-list",
    method: "GET",
    path: "/tickets/:id?",
    category: "Support / Tickets",
    auth: "broker|user",
    title: "List / get tickets",
    consumers: {
      mobile: [],
      web: [
        "src/apis/getintouchApi.js > getSupportList",
        "src/apis/getintouchApi.js > getSupportDetails",
      ],
    },
    controller: "supportController",
  },

  // ───────── Feedback ─────────
  {
    id: "feedback-create",
    method: "POST",
    path: "/feedBack/",
    category: "Feedback",
    auth: "broker|user",
    title: "Submit feedback",
    fstBlocked: true,
    consumers: { mobile: ["api/feedBack.js > feedback"], web: [] },
    controller: "feedBackController",
  },
  {
    id: "feedback-list",
    method: "GET",
    path: "/feedBack/:id?",
    category: "Feedback",
    auth: "JWT",
    title: "List / get feedback",
    consumers: { mobile: [], web: [] },
    controller: "feedBackController",
  },

  // ───────── Interests ─────────
  {
    id: "interests-create",
    method: "POST",
    path: "/interests/:id?",
    category: "Interests",
    auth: "user",
    title: "Express interest in a product",
    description:
      "Creates a CRM opportunity, sends email to broker, queues notification.",
    fstBlocked: true,
    consumers: { mobile: ["api/interest.js > InsertInterest"], web: [] },
    controller: "interestsController",
  },
  {
    id: "interests-list",
    method: "GET",
    path: "/interests/",
    category: "Interests",
    auth: "user",
    title: "My interests",
    consumers: { mobile: [], web: [] },
    controller: "interestsController",
  },

  // ───────── Invite Friends ─────────
  {
    id: "invite-create",
    method: "POST",
    path: "/inviteFriend/",
    category: "Invite Friends",
    auth: "broker|user",
    title: "Send invite email",
    description: "Sends invite email via Azure ACS and awards rewards points.",
    fstBlocked: true,
    consumers: {
      mobile: ["api/inviteFriend.js > referFriend"],
      web: [],
    },
    controller: "inviteFriendController",
  },
  {
    id: "invite-list",
    method: "GET",
    path: "/inviteFriend/:id?",
    category: "Invite Friends",
    auth: "JWT",
    title: "Invite history",
    consumers: {
      mobile: [],
      web: [
        "src/apis/inviteFriendApi.js > getInviteFriendId",
        "src/apis/inviteFriendApi.js > inviteFriendList",
      ],
    },
    controller: "inviteFriendController",
  },

  // ───────── Seek ─────────
  {
    id: "seek-create",
    method: "POST",
    path: "/seek/:id?",
    category: "Seek (Leads)",
    auth: "user",
    title: "Submit seek (lead capture)",
    description: "Creates a CRM lead from 'I am seeking...' flow.",
    consumers: {
      mobile: ["api/seek.js > sendSeekFA"],
      web: [],
    },
    controller: "seekController",
  },
  {
    id: "seek-list",
    method: "GET",
    path: "/seek/",
    category: "Seek (Leads)",
    auth: "JWT",
    title: "Seeks list (admin)",
    consumers: { mobile: [], web: ["src/apis/seeksApi.js > getSeeksServer"] },
    controller: "seekController",
  },

  // ───────── Brokers / Introducers ─────────
  {
    id: "intro-list",
    method: "GET",
    path: "/introducers/:id?",
    category: "Brokers / Introducers",
    auth: "Public",
    title: "List / get introducers (brokers)",
    consumers: {
      mobile: [],
      web: ["src/apis/brokersApis.js > getIntroducers"],
    },
    controller: "introducersController",
  },
  {
    id: "intro-create",
    method: "POST",
    path: "/introducers/",
    category: "Brokers / Introducers",
    auth: "Public",
    title: "Create introducer (broker)",
    consumers: {
      mobile: [],
      web: ["src/apis/brokersApis.js > createIntroducers"],
    },
    controller: "introducersController",
  },
  {
    id: "intro-login",
    method: "POST",
    path: "/introducers/logIn",
    category: "Brokers / Introducers",
    auth: "Public",
    title: "Legacy introducer login",
    consumers: {
      mobile: ["api/existingUserApi.js > generateUserCredential"],
      web: [],
    },
    controller: "introducersController",
  },
  {
    id: "intro-update",
    method: "PUT",
    path: "/introducers/:id",
    category: "Brokers / Introducers",
    auth: "Public",
    title: "Update / soft-delete introducer",
    consumers: {
      mobile: [],
      web: ["src/apis/brokersApis.js > updateIntroducers"],
    },
    controller: "introducersController",
  },
  {
    id: "intro-delete",
    method: "DELETE",
    path: "/introducers/:id",
    category: "Brokers / Introducers",
    auth: "Public",
    title: "Delete introducer",
    consumers: {
      mobile: [],
      web: ["src/apis/brokersApis.js > deleteIntroducers"],
    },
    controller: "introducersController",
  },

  // ───────── Payments ─────────
  {
    id: "payments-card",
    method: "POST",
    path: "/payments/",
    category: "Payments",
    auth: "Public",
    title: "Stripe card PaymentIntent",
    description:
      "Creates a card PaymentIntent + ephemeral key for the Stripe mobile SDK. Auto-creates Stripe customer if missing.",
    consumers: { mobile: ["Used by mobile invest flow (Stripe SDK)"], web: [] },
    controller: "paymentController",
  },
  {
    id: "payments-sepa",
    method: "POST",
    path: "/payments/sepa",
    category: "Payments",
    auth: "Public",
    title: "SEPA Direct Debit PaymentIntent",
    description: "Creates a SEPA PaymentIntent (confirms immediately).",
    consumers: {
      mobile: [],
      web: ["src/apis/paymentAPI.js > createSEPAPayment"],
    },
    controller: "paymentController",
  },
  {
    id: "payments-req-create",
    method: "POST",
    path: "/payments/req",
    category: "Payments",
    auth: "Public",
    title: "Create payment request",
    consumers: {
      mobile: [],
      web: ["src/apis/paymentsReqApis.js > paymentReq"],
    },
    controller: "paymentController",
  },
  {
    id: "payments-req-finalize",
    method: "PUT",
    path: "/payments/req/:slugId",
    category: "Payments",
    auth: "Public",
    title: "Finalize mail-driven payment request",
    description:
      "Accepts paymentType: Online | Offline. Updates paymentRequests, mailRequests. Stores receipt for offline payments in paymentDocuments.",
    consumers: { mobile: [], web: ["src/apis/paymentAPI.js > updatePayment"] },
    controller: "paymentController / mailRequestController",
  },

  // ───────── Requests / Mail ─────────
  {
    id: "requests-get",
    method: "GET",
    path: "/requests/:slug",
    category: "Requests / Mail",
    auth: "Public",
    title: "Get mail-request by slug",
    description: "Used by the payment-request link emailed to the investor.",
    consumers: {
      mobile: [],
      web: [
        "src/apis/requestsApi.js > getRequest",
        "src/apis/docusealApi.js > sendDocuseal",
      ],
    },
    controller: "mailRequestController",
  },
  {
    id: "requests-create",
    method: "POST",
    path: "/requests/",
    category: "Requests / Mail",
    auth: "broker",
    title: "Broker sends payment-request email",
    consumers: { mobile: [], web: [] },
    controller: "mailRequestController",
  },

  // ───────── Analytics ─────────
  {
    id: "analytics-graph",
    method: "GET",
    path: "/analytics/graph",
    category: "Analytics",
    auth: "JWT",
    title: "Registered-user graph",
    consumers: {
      mobile: [],
      web: ["src/apis/dashboardApi.js > getRegisteredUsers"],
    },
    controller: "analyticsController",
  },
  {
    id: "analytics-downloads",
    method: "GET",
    path: "/analytics/downloads",
    category: "Analytics",
    auth: "JWT",
    title: "App downloads",
    consumers: {
      mobile: [],
      web: ["src/apis/dashboardApi.js > getDownloadsData"],
    },
    controller: "analyticsController",
  },
  {
    id: "analytics-history",
    method: "GET",
    path: "/analytics/history",
    category: "Analytics",
    auth: "JWT",
    title: "Login-history graph",
    consumers: {
      mobile: [],
      web: ["src/apis/dashboardApi.js > getLoginsData"],
    },
    controller: "analyticsController",
  },

  {
    id: "analytics-usage-overview",
    method: "GET",
    path: "/admin/analytics/usage/overview",
    category: "Analytics (PostHog)",
    auth: "JWT (admin)",
    title: "App usage overview (DAU, WAU, KPIs)",
    consumers: {
      mobile: [],
      web: ["src/apis/analyticsUsageApi.js > getUsageOverview"],
    },
    controller: "posthogInsights",
  },
  {
    id: "analytics-usage-active-users",
    method: "GET",
    path: "/admin/analytics/usage/active-users",
    category: "Analytics (PostHog)",
    auth: "JWT (admin)",
    title: "Daily / weekly active users",
    queryParams: "from, to, granularity (day|week)",
    consumers: {
      mobile: [],
      web: ["src/apis/analyticsUsageApi.js > getUsageActiveUsers"],
    },
    controller: "posthogInsights",
  },
  {
    id: "analytics-usage-page-views",
    method: "GET",
    path: "/admin/analytics/usage/page-views",
    category: "Analytics (PostHog)",
    auth: "JWT (admin)",
    title: "Page views by screen",
    consumers: {
      mobile: [],
      web: ["src/apis/analyticsUsageApi.js > getUsagePageViews"],
    },
    controller: "posthogInsights",
  },
  {
    id: "analytics-usage-products",
    method: "GET",
    path: "/admin/analytics/usage/products",
    category: "Analytics (PostHog)",
    auth: "JWT (admin)",
    title: "Product views, CTA & investments",
    consumers: {
      mobile: [],
      web: ["src/apis/analyticsUsageApi.js > getUsageProducts"],
    },
    controller: "posthogInsights",
  },
  {
    id: "analytics-usage-funnels",
    method: "GET",
    path: "/admin/analytics/usage/funnels/*",
    category: "Analytics (PostHog)",
    auth: "JWT (admin)",
    title: "Engagement, investment & auth funnels",
    consumers: {
      mobile: [],
      web: ["src/apis/analyticsUsageApi.js"],
    },
    controller: "posthogInsights",
  },
  {
    id: "analytics-usage-retention",
    method: "GET",
    path: "/admin/analytics/usage/retention",
    category: "Analytics (PostHog)",
    auth: "JWT (admin)",
    title: "Cohort retention (page_view)",
    consumers: {
      mobile: [],
      web: ["src/apis/analyticsUsageApi.js > getUsageRetention"],
    },
    controller: "posthogInsights",
  },

  // ───────── Dashboard (ETL header-token) ─────────
  {
    id: "dashboard-signups",
    method: "GET",
    path: "/dashboard/signups",
    category: "Dashboard (ETL)",
    auth: "Header token (gm_live_...)",
    title: "Daily signups (Power BI)",
    description:
      "Header-token auth (lib/dashboardAuth.js). Returns daily signup counts grouped by role + email.",
    consumers: { mobile: [], web: ["External Power BI / ETL only"] },
    controller: "dashboardController",
  },
  {
    id: "dashboard-logins",
    method: "GET",
    path: "/dashboard/logins",
    category: "Dashboard (ETL)",
    auth: "Header token (gm_live_...)",
    title: "Daily logins (Power BI)",
    consumers: { mobile: [], web: ["External Power BI / ETL only"] },
    controller: "dashboardController",
  },
  {
    id: "dashboard-failed-logins",
    method: "GET",
    path: "/dashboard/failed-logins",
    category: "Dashboard (ETL)",
    auth: "Header token (gm_live_...)",
    title: "Daily failed logins (Power BI)",
    consumers: { mobile: [], web: ["External Power BI / ETL only"] },
    controller: "dashboardController",
  },

  // ───────── Admin / FST ─────────
  {
    id: "admin-fst-list",
    method: "GET",
    path: "/admin/fst/",
    category: "Admin / FST",
    auth: "admin",
    title: "List FST users",
    consumers: {
      mobile: [],
      web: ["src/apis/fstUsersApi.js > getFstUsersList"],
    },
    controller: "fstController",
  },
  {
    id: "admin-fst-create",
    method: "POST",
    path: "/admin/fst/",
    category: "Admin / FST",
    auth: "admin",
    title: "Create FST user",
    description:
      "Stores isFST=true, role 'fst'. Validated by validateCreateFstUser.",
    consumers: { mobile: [], web: ["src/apis/fstUsersApi.js > createFstUser"] },
    controller: "fstController",
  },
  {
    id: "admin-fst-assignments",
    method: "GET",
    path: "/admin/fst/assignments",
    category: "Admin / FST",
    auth: "admin",
    title: "List FST assignments",
    consumers: {
      mobile: [],
      web: ["src/apis/fstAssignmentsApi.js > getFstAssignments"],
    },
    controller: "fstController",
  },
  {
    id: "admin-fst-logs",
    method: "GET",
    path: "/admin/fst/assignments/logs",
    category: "Admin / FST",
    auth: "admin",
    title: "FST assignment audit log",
    consumers: {
      mobile: [],
      web: ["src/apis/fstAssignmentsApi.js > getFstAssignmentLogs"],
    },
    controller: "fstController",
  },
  {
    id: "admin-fst-assign",
    method: "POST",
    path: "/admin/fst/assign",
    category: "Admin / FST",
    auth: "admin",
    title: "Assign FST user (2-step OTP)",
    description:
      "First call sends OTP to admin via Twilio; second call (with otp) creates the assignment, deactivates prior active ones, copies CRM context onto the FST user record.",
    consumers: {
      mobile: [],
      web: ["src/apis/fstAssignmentsApi.js > assignFstUser"],
    },
    controller: "fstController",
  },
  {
    id: "admin-fst-assignment-inactive",
    method: "PUT",
    path: "/admin/fst/assignments/:assignmentId/inactive",
    category: "Admin / FST",
    auth: "admin",
    title: "Inactivate an FST assignment",
    consumers: {
      mobile: [],
      web: ["src/apis/fstAssignmentsApi.js > inactivateFstAssignment"],
    },
    controller: "fstController",
  },
  {
    id: "admin-fst-user-inactive",
    method: "PUT",
    path: "/admin/fst/:id/inactive",
    category: "Admin / FST",
    auth: "admin",
    title: "Inactivate FST user",
    description:
      "Sets status I, inactivates all active assignments, clears CRM context.",
    consumers: {
      mobile: [],
      web: ["src/apis/fstUsersApi.js > inactivateFstUser"],
    },
    controller: "fstController",
  },
  {
    id: "admin-fst-password",
    method: "PUT",
    path: "/admin/fst/:id/password",
    category: "Admin / FST",
    auth: "admin",
    title: "Set FST user password",
    consumers: {
      mobile: [],
      web: ["src/apis/fstUsersApi.js > changeFstUserPassword"],
    },
    controller: "fstController",
  },

  // ───────── Webhooks ─────────
  {
    id: "wh-investor",
    method: "POST",
    path: "/broker/investor",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "CRM investor push",
    description:
      "Pushes a fetchInvestor job to contact_queue. Aliased as /crm/investor.",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.createCrmInvestor",
  },
  {
    id: "wh-introducer",
    method: "POST",
    path: "/broker/introducer",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "CRM introducer push",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.createCrmIntroducer",
  },
  {
    id: "wh-investment",
    method: "POST",
    path: "/broker/investment",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "CRM investment sync",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.createCrmInvestment",
  },
  {
    id: "wh-opp-investment",
    method: "POST",
    path: "/broker/opp/investment",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "CRM won-opportunity investment",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.CrmWonInvestment",
  },
  {
    id: "wh-opportunity",
    method: "POST",
    path: "/broker/opportunity",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "CRM opportunity push",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.createCrmOpportunity",
  },
  {
    id: "wh-eltif-investment",
    method: "POST",
    path: "/broker/eltif/investment",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "CRM ELTIF investment",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.createEltifInvestment",
  },
  {
    id: "wh-broker",
    method: "POST",
    path: "/broker/broker",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "Sync broker account info (used by mobile)",
    consumers: { mobile: ["api/user.js > getBrokerDetails"], web: [] },
    controller: "webhookController.crmBrokerInfoMiddleWare",
  },
  {
    id: "wh-gmi-request",
    method: "POST",
    path: "/broker/gmi/request",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "Lookup existing CRM contact (used by mobile)",
    consumers: { mobile: ["api/user.js > requestCredentials"], web: [] },
    controller: "webhookController.gmiInfoMiddleWare",
  },
  {
    id: "wh-docuseal",
    method: "POST",
    path: "/broker/docuseal",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "DocuSeal completed webhook",
    description:
      "Sets subscriptionSigns.status = Completed, persists signed PDF URL + audit log, emails investor, kicks off CRM document upload.",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.docusealCompleteMiddleWare",
  },
  {
    id: "wh-sendsign",
    method: "POST",
    path: "/broker/sendsign",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "CRM-requested digital signature",
    description: "Enqueues sendSign to docuseal_queue with 100s delay.",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.docusealCRMSignatureMiddleware",
  },
  {
    id: "wh-funds-approved",
    method: "POST",
    path: "/broker/fundsApproved",
    category: "Webhooks (CRM / DocuSeal)",
    auth: "Public (network-protected)",
    title: "Redemption funds approved",
    description: "Enqueues redeemFundsApproved on redemption_queue.",
    consumers: { mobile: [], web: [] },
    controller: "webhookController.redemptionFundsApprovedMiddleWare",
  },

  // ───────── Health / Misc ─────────
  {
    id: "ping",
    method: "GET",
    path: "/ping",
    category: "Health / Misc",
    auth: "Public",
    title: "Health probe",
    description: "Returns 'pong'. Use for load-balancer health checks.",
    consumers: { mobile: [], web: [] },
    controller: "—",
  },
  {
    id: "users-test",
    method: "GET",
    path: "/users/test",
    category: "Health / Misc",
    auth: "Public",
    title: "Load test endpoint",
    consumers: { mobile: [], web: [] },
    controller: "userController.testForLoad",
  },
];

export default APIS;
